import React, { FC } from 'react';
import { Paper, Typography, Box, Grid, Alert } from '@mui/material';
import { Order, OrderProduct, OrderAddress, ShippingAddress, OrderMetafield, LoyaltyDetails } from '@models';
import moment from 'moment';
import { OrderStatuses, CartTotalCode } from '@models';
import { useAppSelector } from '@hooks/redux-custom';

type Props = {
  order: Order;
  orderProducts: OrderProduct[];
  shippingAddress: ShippingAddress[];
  metaFields: OrderMetafield[] | undefined;
  loyalty: LoyaltyDetails;
};

type InvoiceTotal = {
  code: string;
  value: number;
  title: string;
};

const OrderInvoice: FC<Props> = ({ order, orderProducts, shippingAddress, metaFields, loyalty }) => {
  const getInvoiceData = metaFields?.find((item) => item.key === 'invoiceData');
  const pwaInvoiceData = getInvoiceData?.value ? JSON.parse(getInvoiceData?.value) : undefined;

  const driverTip = pwaInvoiceData?.items
    ?.filter((product: any) => product.sku === 'TIP000')
    .map((product: any) => product.rowTotalInclTax);

  const invShippingAddress: ShippingAddress | OrderAddress = shippingAddress.length ? shippingAddress[0] : order.billing_address;
  const isRefundExist = metaFields?.find((item) => item.key.includes('refund'));
  const customer = useAppSelector((state) => state.customer.value.selectedCustomer);

  const isOrderRefunded = () => {
    return order.status === OrderStatuses.PartiallyRefunded || order.status === OrderStatuses.Refunded;
  };

  const getDataByCode = (code: string): InvoiceTotal => {
    return pwaInvoiceData?.totals?.find((total: InvoiceTotal) => total.code === code)?.value ?? 0;
  };

  return (
    <Paper data-testid="order-invoice">
      <Grid container p={2}>
        {order.status.toLowerCase().includes('refunded') && (
          <Grid item xs={12} mb={2}>
            {isRefundExist?.key ? (
              <Alert severity="success">${order.status} has been processed</Alert>
            ) : (
              <Alert severity="warning">Refund not yet processed</Alert>
            )}
          </Grid>
        )}
        <Grid item xs={12}>
          <Typography variant="h3">Jimmy Brings</Typography>
          <Typography variant="h6" data-testid="order-id-text">
            Order - {order.id}
          </Typography>
        </Grid>

        <Grid item xs={6} data-testid="customer-billing-info">
          <Box bgcolor="primary.lighter" p={2}>
            <Typography variant="h6"> Billing Address </Typography>
            <Typography variant="subtitle1" data-testid="invoice-billing-address-name-text">
              {' '}
              {`${order.billing_address.first_name} ${order.billing_address.last_name}`}{' '}
            </Typography>
            <Typography variant="body1" data-testid="invoice-billing-address-street-text">
              {order.billing_address.street_2 ? `${order.billing_address.street_2},` : ''} {order.billing_address.street_1}
            </Typography>
            <Typography variant="body1" data-testid="invoice-billing-address-city-text">
              {order.billing_address.city},
            </Typography>
            <Typography variant="body1" data-testid="invoice-billing-address-state-zip-text">
              {order.billing_address.state} - {order.billing_address.zip}
            </Typography>
            <Typography variant="body1" data-testid="invoice-billing-address-phone-text">
              Phone: {order.billing_address.phone ? order.billing_address.phone : customer.phone}
            </Typography>
            <Typography variant="body1">OrderId: {order.id}</Typography>
            <Typography variant="body1" data-testid="invoice-payment-method-text">
              Payment method: {order.payment_method}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box bgcolor="primary.lighter" p={2}>
            <Typography variant="h6"> Shipping Address </Typography>
            <Typography variant="subtitle1" data-testid="invoice-shipping-address-name-text">
              {' '}
              {`${invShippingAddress.first_name} ${invShippingAddress.last_name}`}{' '}
            </Typography>
            <Typography variant="body1" data-testid="invoice-shipping-address-street-text">
              {invShippingAddress.street_2 ? `${invShippingAddress.street_2},` : ''} {invShippingAddress.street_1}
            </Typography>
            <Typography variant="body1" data-testid="invoice-shipping-address-city-text">
              {invShippingAddress.city},
            </Typography>
            <Typography variant="body1" data-testid="invoice-shipping-address-state-zip-text">
              {invShippingAddress.state} - {invShippingAddress.zip}
            </Typography>
            <Typography variant="body1" data-testid="invoice-shipping-address-phone-text">
              Phone: {invShippingAddress.phone ? invShippingAddress.phone : customer.phone}
            </Typography>
            <Typography variant="body1" data-testid="order-date-text">
              Order Date: {moment(order.date_created).format('DD MMM YYYY')}
            </Typography>
            <Typography variant="body1">Shipping Method: Delivery</Typography>
          </Box>
        </Grid>
        <hr />
        <Grid item xs={12} p={2}>
          <Grid container alignItems="center">
            <Grid item xs={12}>
              <Typography variant="h6">Order Items</Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="caption">Qty</Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="caption">Sku</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption">Product Name</Typography>
            </Grid>
            <Grid item xs={2} textAlign="right">
              <Typography variant="caption">Price</Typography>
            </Grid>
            <Grid item xs={2} textAlign="right">
              <Typography variant="caption">Total</Typography>
            </Grid>
          </Grid>
          <hr />
          {pwaInvoiceData
            ? pwaInvoiceData?.items
                ?.filter((product: any) => product.sku !== 'TIP000')
                .map((product: any) => {
                  const discount = product.rowTotalWithDiscount < product.rowTotalInclTax;
                  const promotion = product.promotions?.length ? product.promotions[0] : undefined;
                  return (
                    <React.Fragment key={product.id}>
                      <Grid container>
                        <Grid item xs={2}>
                          <Typography variant="body1" data-testid="invoice-pwa-prod-qty">
                            {product.qty}
                          </Typography>
                        </Grid>
                        <Grid item xs={2}>
                          <Typography variant="body1" data-testid="invoice-pwa-prod-sku">
                            {product.sku}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="body1" data-testid="invoice-pwa-prod-name">
                            {product.name}
                          </Typography>
                        </Grid>
                        <Grid item xs={2}>
                          <Typography variant="body1" textAlign="right" data-testid="invoice-pwa-prod-price">
                            {promotion?.amount > 0 ? Number(product.price).toFixed(2) : Number(product.regularPrice).toFixed(2)}
                          </Typography>
                        </Grid>
                        <Grid item xs={2}>
                          <Typography variant="body1" textAlign="right" data-testid="invoice-data-row-total">
                            {promotion?.amount > 0 || discount
                              ? Number(product.rowTotalWithDiscount).toFixed(2)
                              : Number(product.rowTotalInclTax).toFixed(2)}
                          </Typography>
                        </Grid>
                      </Grid>
                      {promotion?.amount > 0 && (
                        <Grid container key={product.id}>
                          <Grid item xs={2}></Grid>
                          <Grid item xs={2}></Grid>
                          <Grid item xs={4}>
                            <Typography variant="caption">{promotion?.name}</Typography>
                          </Grid>
                          <Grid item xs={2} textAlign="right">
                            <Typography
                              variant="caption"
                              style={{
                                textDecoration: 'line-through',
                              }}
                            >
                              {Number(product.regularPrice).toFixed(2)}
                            </Typography>
                          </Grid>
                          <Grid item xs={2} textAlign="right">
                            <Typography
                              variant="caption"
                              style={{
                                textDecoration: 'line-through',
                              }}
                            >
                              {Number(product.rowTotalInclTax).toFixed(2)}
                            </Typography>
                          </Grid>
                        </Grid>
                      )}
                    </React.Fragment>
                  );
                })
            : orderProducts.map((prod: OrderProduct) => (
                <Grid container key={prod.id}>
                  <Grid item xs={2}>
                    <Typography variant="body1" data-testid="invoice-jimbo-prod-qty">
                      {prod.quantity}
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography variant="body1" data-testid="invoice-jimbo-prod-sku">
                      {prod.sku}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body1" data-testid="invoice-jimbo-prod-name">
                      {prod.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography variant="body1" data-testid="invoice-jimbo-prod-price">
                      {Number(prod.price_inc_tax).toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography variant="body1" textAlign="right" data-testid="invoice-jimbo-prod-price">
                      {Number(prod.total_inc_tax).toFixed(2)}
                    </Typography>
                  </Grid>
                </Grid>
              ))}
          <hr />
        </Grid>
        <Grid container p={2}>
          <Grid item xs={9}></Grid>
          <Grid item xs={2}>
            <Typography variant="subtitle1">Cart Total:</Typography>
            {Number(driverTip) > 0 && <Typography variant="subtitle1">Driver Tip:</Typography>}
            <Typography variant="subtitle1">Delivery Fee:</Typography>
            {Number(order.discount_amount) > 0 && <Typography variant="subtitle1">Discount:</Typography>}
            {pwaInvoiceData?.promotions.length > 0 && (
              <>
                <Typography variant="subtitle1">
                  Promotion{' '}
                  <Typography variant="caption" data-testid="invoice-pwa-coupon-code">
                    ({pwaInvoiceData?.promotions[0].couponCode})
                  </Typography>
                  :
                </Typography>
              </>
            )}
            <Typography variant="subtitle1">Order Total:</Typography>
            <Typography variant="caption">Includes GST:</Typography>
            {isOrderRefunded() && (
              <Typography variant="subtitle1" data-testid="invoice-order-status">
                {order.status}
              </Typography>
            )}
          </Grid>
          <Grid item xs={1}>
            <Typography variant="subtitle1" textAlign="right" data-testid="invoice-order-sub-total">
              {pwaInvoiceData ? Number(getDataByCode(CartTotalCode.subtotal)).toFixed(2) : Number(order.subtotal_inc_tax).toFixed(2)}
            </Typography>

            {Number(driverTip) > 0 && (
              <Typography variant="subtitle1" textAlign="right" data-testid="invoice-driver-tip">
                {Number(driverTip).toFixed(2)}
              </Typography>
            )}

            <Typography variant="subtitle1" textAlign="right" data-testid="invoice-order-shipping-cost">
              {pwaInvoiceData ? Number(getDataByCode(CartTotalCode.shipping)).toFixed(2) : Number(order.shipping_cost_inc_tax).toFixed(2)}
            </Typography>

            {Number(order.discount_amount) > 0 && (
              <Typography variant="subtitle1" textAlign="right" data-testid="order-discount-amount">
                -{Number(order.discount_amount).toFixed(2)}
              </Typography>
            )}

            {pwaInvoiceData?.promotions.length > 0 && (
              <>
                <Typography variant="subtitle1" textAlign="right" data-testid="invoice-pwa-promotion-amount">
                  - ${pwaInvoiceData?.promotions[0].amount}
                </Typography>
                <Typography variant="subtitle1"> </Typography>
              </>
            )}

            <Typography variant="subtitle1" textAlign="right" data-testid="order-total-amount">
              {pwaInvoiceData ? Number(getDataByCode(CartTotalCode.grand_total)).toFixed(2) : Number(order.total_inc_tax).toFixed(2)}
            </Typography>

            <Typography variant="caption" textAlign="right" component="div" data-testid="invoice-order-total-tax">
              {pwaInvoiceData ? Number(getDataByCode(CartTotalCode.tax)).toFixed(2) : Number(order.total_tax).toFixed(2)}
            </Typography>

            {isOrderRefunded() && (
              <Typography variant="subtitle1" textAlign="right" component="div" data-testid="invoice-order-refunded-amount">
                {Number(order.refunded_amount).toFixed(2)}
              </Typography>
            )}
          </Grid>
        </Grid>
        {Object.values(loyalty).some((value) => value !== '') && (
          <Grid item xs={12} p={2}>
            <Box bgcolor="primary.lighter">
              <Grid item xs={12} data-testid="loyalty-heading" p={1}>
                <Typography variant="h6"> Loyalty</Typography>
              </Grid>
              <Grid item xs={4} data-testid="loyalty-voucher-value-updated-to" p={1}>
                <Typography variant="body1">{` Voucher value updated to: $ ${loyalty.loyalty_voucher_value}`}</Typography>
              </Grid>
              <Grid item xs={4} data-testid="loyalty-order-count-updated-to" p={1}>
                <Typography variant="body1">{` Order count updated to: ${loyalty.loyalty_order_count}`}</Typography>
              </Grid>
              {loyalty.loyalty_coupon && (
                <Grid item xs={4} data-testid="generated-loyalty-coupon" p={1}>
                  <Typography variant="body1">{` Coupon generated: ${loyalty.loyalty_coupon}`}</Typography>
                </Grid>
              )}
            </Box>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default OrderInvoice;
